import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import axios from "axios";
import { Button } from "components/button";
import Spinner from "components/spinner";

console.log("🔥 DownloadFiles component file loaded!");

const DownloadFiles = () => {
	console.log("🚀 DownloadFiles component is rendering!");
	const location = useLocation();
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(true);
	const [isDownloading, setIsDownloading] = useState(false);
	const [error, setError] = useState("");
	const [fileData, setFileData] = useState<any>(null);
	const [userId, setUserId] = useState<string>("");
	const [companyId, setCompanyId] = useState<string>("");

	const [downloadOptions, setDownloadOptions] = useState<{
		customer: boolean;
		request: boolean;
		property: boolean;
	}>({
		customer: false,
		request: false,
		property: false,
	});

	useEffect(() => {
		const params = queryString.parse(location.search);
		const userIdParam = params.user_id as string;
		const companyIdParam = params.company_id as string;
		const customerParam = params.customer === "true";
		const requestParam = params.request === "true";
		const propertyParam = params.property === "true";
		const tokenParam = params.token as string;

		if (!userIdParam || !companyIdParam) {
			setError("Invalid download link. Missing required parameters.");
			setIsLoading(false);
			return;
		}

		setUserId(userIdParam);
		setCompanyId(companyIdParam);
		setDownloadOptions({
			customer: customerParam,
			request: requestParam,
			property: propertyParam,
		});

		console.log("tokenParam: ", tokenParam);

		// Fetch file information
		fetchFileInfo(userIdParam, companyIdParam, customerParam, requestParam, propertyParam, tokenParam);
	}, [location.search]);

	const fetchFileInfo = async (
		userId: string,
		companyId: string,
		customer: boolean,
		request: boolean,
		property: boolean,
		token: string
	) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business-unprotected/admin/download-with-token`,
				{
					params: {
						user_id: userId,
						company_id: companyId,
						customer: customer,
						request: request,
						property: property,
						token: token,
					},
				}
			);

			if (response.status === 200) {
				setFileData(response.data);
			}
		} catch (error: any) {
			console.error("Error fetching file info:", error);
			if (error.response?.status === 403) {
				setError("You do not have permission to access these files.");
			} else if (error.response?.status === 404) {
				setError("No files found for download.");
			} else {
				setError("Failed to load file information. Please try again later.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleDownload = async () => {
		setIsDownloading(true);
		setError("");
	
		try {
			// Extract token from URL params
			const params = queryString.parse(location.search);
			const tokenParam = params.token as string;
	
			const response = await axios.get(
				`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business-unprotected/admin/download-with-token`,
				{
					params: {
						user_id: userId,
						company_id: companyId,
						customer: downloadOptions.customer,
						request: downloadOptions.request,
						property: downloadOptions.property,
						token: tokenParam,  // Pass the token!
					},
					responseType: "blob",
				}
			);
	
			// Create a blob URL and trigger download
			const blob = new Blob([response.data], {
				type: response.headers["content-type"],
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `company-${companyId}-files.zip`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			console.error("Error downloading files:", error);
			if (error.response?.status === 403) {
				setError("You do not have permission to download these files.");
			} else if (error.response?.status === 401) {
				setError("Download link has expired. Please request a new link.");
			} else {
				setError("Failed to download files. Please try again.");
			}
		} finally {
			setIsDownloading(false);
		}
	};

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<Spinner />
					<p className="mt-4 text-gray-600">Loading file information...</p>
				</div>
			</div>
		);
	}

	console.log("fileData: ", fileData);
	console.log("error: ", error);

	if (error && !fileData) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
					<div className="text-center">
						<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
							<svg
								className="h-6 w-6 text-red-600"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</div>
						<h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
						<p className="text-gray-600 mb-6">{error}</p>
						<Button onClick={() => history.push("/login")}>Go to Login</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-3xl mx-auto">
				<div className="bg-white rounded-lg shadow-lg overflow-hidden">
					{/* Header */}
					<div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
						<div className="flex items-center justify-center mb-4">
							<svg
								className="h-16 w-16 text-white"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
								/>
							</svg>
						</div>
						<h1 className="text-3xl font-bold text-white text-center">File Download</h1>
						<p className="text-blue-100 text-center mt-2">
							Your files are ready for download
						</p>
					</div>

					{/* Content */}
					<div className="px-6 py-8">
						<div className="mb-8">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								Download Information
							</h2>
							<div className="bg-gray-50 rounded-lg p-4 space-y-2">
								<div className="flex justify-between items-center">
									<span className="text-sm font-medium text-gray-600">Company ID:</span>
									<span className="text-sm text-gray-900">{companyId}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-sm font-medium text-gray-600">User ID:</span>
									<span className="text-sm text-gray-900">{userId}</span>
								</div>
								{fileData?.fileCount && (
									<div className="flex justify-between items-center">
										<span className="text-sm font-medium text-gray-600">Files Available:</span>
										<span className="text-sm text-gray-900">{fileData.fileCount}</span>
									</div>
								)}
								{fileData?.totalSize && (
									<div className="flex justify-between items-center">
										<span className="text-sm font-medium text-gray-600">Total Size:</span>
										<span className="text-sm text-gray-900">{fileData.totalSize}</span>
									</div>
								)}
							</div>
						</div>

					<div className="mb-8">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">File Types Included:</h3>
						<div className="space-y-2">
							{downloadOptions.customer && (
								<div className="flex items-center space-x-2 text-sm">
									<svg
										className="w-5 h-5 text-green-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700 font-medium">Customer Files</span>
								</div>
							)}
							{downloadOptions.request && (
								<div className="flex items-center space-x-2 text-sm">
									<svg
										className="w-5 h-5 text-green-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700 font-medium">Request Files</span>
								</div>
							)}
							{downloadOptions.property && (
								<div className="flex items-center space-x-2 text-sm">
									<svg
										className="w-5 h-5 text-green-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700 font-medium">Property Files</span>
								</div>
							)}
							{!downloadOptions.customer &&
								!downloadOptions.request &&
								!downloadOptions.property && (
									<p className="text-sm text-gray-500 italic">
										All file types will be included in the download
									</p>
								)}
						</div>
					</div>

					{fileData?.description && (
						<div className="mb-8">
							<h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
							<p className="text-sm text-gray-600">{fileData.description}</p>
						</div>
					)}

						<div className="space-y-4">
							<Button
								primary
								onClick={handleDownload}
								disabled={isDownloading}
								className="w-full"
							>
								{isDownloading ? (
									<span className="flex items-center justify-center">
										<svg
											className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											></path>
										</svg>
										Downloading...
									</span>
								) : (
									<span className="flex items-center justify-center">
										<svg
											className="w-5 h-5 mr-2"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
											/>
										</svg>
										Download Files
									</span>
								)}
							</Button>

							{error && (
								<div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
									<div className="flex">
										<div className="flex-shrink-0">
											<svg
												className="h-5 w-5 text-red-400"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
												/>
											</svg>
										</div>
										<div className="ml-3">
											<p className="text-sm">{error}</p>
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="mt-8 pt-6 border-t border-gray-200">
							<p className="text-xs text-gray-500 text-center">
								This is a secure download link. If you did not request this download, please
								contact your administrator.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DownloadFiles;


